import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Description as DocumentIcon,
  InsertDriveFile as FileIcon,
  FilterList as FilterIcon,
  Image as ImageIcon,
  MoreVert as MoreVertIcon,
  PictureAsPdf as PdfIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  VideoFile as VideoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import React, { useState } from 'react';

import Sidebar from '../../components/Sidebar';
import { useNavigate } from 'react-router';
import { useProfile } from '../../providers/ProfileProvider';

const mockMyFiles = [
  {
    id: 1,
    name: 'Signed Employment Contract',
    description: 'My signed employment contract.',
    filename: 'employment_contract_2024.pdf',
    size: '2.4 MB',
    date: '2024-01-15',
    category: 'Contracts',
    type: 'pdf'
  },
  {
    id: 2,
    name: 'Consultant CV',
    description: 'Updated requirements for Q1 project. Please confirm understanding.',
    filename: 'project_specs_v2.docx',
    size: '1.8 MB',
    date: '2024-01-10',
    category: 'Templates',
    type: 'document'
  },
  {
    id: 3,
    name: 'Profile Photo',
    description: 'My profile photo - for personalization',
    filename: 'my_photo.jpg',
    size: '5.2 MB',
    date: '2024-01-05',
    category: 'Media',
    type: 'image'
  }
];

const mockNativeFiles = [
  {
    id: 1,
    name: 'Employment Contract',
    description: 'Please review before end of next week. BR Jonas',
    filename: 'employment_contract_2024.pdf',
    size: '2.4 MB',
    date: '2024-01-15',
    uploadedBy: 'Jonas Nielsen',
    category: 'Contracts',
    type: 'pdf',
    expiresIn: 18,
    status: 'active'
  },
  {
    id: 2,
    name: 'Project Specifications',
    description: 'Updated requirements for Q1 project. Please confirm understanding.',
    filename: 'project_specs_v2.docx',
    size: '1.8 MB',
    date: '2024-01-10',
    uploadedBy: 'Sarah Wilson',
    category: 'Projects',
    type: 'document',
    expiresIn: 13,
    status: 'active'
  },
  {
    id: 3,
    name: 'Team Photo',
    description: 'Company retreat 2024 - for website use',
    filename: 'team_retreat_2024.jpg',
    size: '5.2 MB',
    date: '2024-01-05',
    uploadedBy: 'Mike Johnson',
    category: 'Media',
    type: 'image',
    expiresIn: 8,
    status: 'active'
  },
  {
    id: 4,
    name: 'Training Video',
    description: 'Onboarding material for new developers',
    filename: 'dev_onboarding.mp4',
    size: '45.6 MB',
    date: '2023-12-20',
    uploadedBy: 'Anna Berg',
    category: 'Training',
    type: 'video',
    expiresIn: 0,
    status: 'expired'
  }
];

const FileManager = () => {
  const navigate = useNavigate();
  const profile = useProfile();
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getCurrentFiles = () => {
    return activeTab === 0 ? mockMyFiles : mockNativeFiles;
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf':
        return <PdfIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
      case 'image':
        return <ImageIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
      case 'document':
        return <DocumentIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
      case 'video':
        return <VideoIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
      default:
        return <FileIcon sx={{ fontSize: 28, color: "text.secondary" }} />;
    }
  };

  const MyFilesCard = ({ file }) => (
    <Paper
      elevation={1}
      sx={{
        p: 1,
        mb: 1,
        borderRadius: 2,
        backgroundColor: '#ffffff',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        minHeight: '80px',
        '&:hover': {
          boxShadow: 2
        }
      }}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>
        {/* File Icon */}
        <Box sx={{ mr: 2, mt: 0.5 }}>
          {getFileIcon(file.type)}
        </Box>

        {/* File Details */}
        <Box sx={{ flex: 1 }}>
          {/* File Name and Category */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '12px' }}>
              {file.name}
              <Chip
                label={file.category}
                size="small"
                sx={{
                  backgroundColor: '#f8f9fa',
                  color: '#6c757d',
                  fontSize: '6px',
                  height: '12px',
                  fontWeight: 500,
                  borderRadius: '100px',
                  border: '1px solid #dee2e6',
                  ml: 1
                }}
              />
            </Typography>
          </Box>

          {/* Description */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, lineHeight: 1, fontSize: '10px' }}
          >
            {file.description}
          </Typography>

          {/* File metadata */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FileIcon sx={{ fontSize: 13 }} />
              <Typography variant="caption" color="text.secondary">
                {file.filename} {file.size}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {file.date}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Actions Menu */}
      <IconButton
        size="small"
        sx={{ ml: 1, alignSelf: 'flex-start' }}
      >
        <MoreVertIcon />
      </IconButton>
    </Paper>
  );

  const NativeFilesCard = ({ file }) => (
    <Box>
      <Paper
        elevation={1}
        sx={{
          p: 1,
          mb: file.status === 'expired' ? 0 : 1,
          borderRadius: 2,
          backgroundColor: '#ffffff',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          minHeight: '80px',
          '&:hover': {
            boxShadow: 2
          }
        }}
      >
        <Box sx={{ display: 'flex', flex: 1 }}>
          {/* File Icon */}
          <Box sx={{ mr: 2, mt: 0.5 }}>
            {getFileIcon(file.type)}
          </Box>
         {/* File Details */}
          <Box sx={{ flex: 1 }}>
            {/* File Name and Category */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '12px' }}>
                {file.name}
                <Chip
                  label={file.category}
                  size="small"
                  sx={{
                    backgroundColor: '#f8f9fa',
                    color: '#6c757d',
                    fontSize: '6px',
                    height: '12px',
                    fontWeight: 500,
                    borderRadius: '100px',
                    border: '1px solid #dee2e6',
                    ml: 1
                  }}
                />
              </Typography>
              {/* Expiration Status */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {file.status === 'expired' ? (
                  <Chip
                    label="Expired"
                    size="small"
                    sx={{
                      backgroundColor: '#ffebee',
                      color: '#d32f2f',
                      fontSize: '6px',
                      height: '12px',
                      fontWeight: 500,
                      borderRadius: '100px',
                      border: '1px solid #ffcdd2'
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '8px' }}>
                    Expires in {file.expiresIn} days
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Description */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, lineHeight: 1, fontSize: '10px' }}
            >
              {file.description}
            </Typography>

            {/* File metadata */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FileIcon sx={{ fontSize: 13 }} />
                <Typography variant="caption" color="text.secondary">
                  {file.filename} {file.size}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {file.uploadedBy}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {file.date}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Actions Menu */}
        <IconButton
          size="small"
          sx={{ ml: 1, alignSelf: 'flex-start' }}
        >
          <MoreVertIcon />
        </IconButton>
      </Paper>
      {/* Expiration Warning */}
      {file.status === 'expired' && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            mt: 1,
            backgroundColor: '#FDEDED',
            color: '#5F2120',
            '& .MuiAlert-message': {
              fontSize: '11px'
            }
          }}
        >
          This file has expired and will be permanently removed soon. If you believe this file was removed by mistake or should have a longer expiry date, please contact your manager or system support.
        </Alert>
      )}
    </Box>
  );

  const files = getCurrentFiles();
  return (
    <div>
      <Sidebar currentPath="/" />
      {/* Main Content Area */}
      <div
        style={{
          position: 'fixed',
          left: '258px',
          width: '85vw',
          height: '95vh',
          overflow: 'auto',
          padding: '24px 32px',
          boxSizing: 'border-box',
          background: '#EAE7E6'
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '0.5rem' }}>
          <Typography
            variant="h3"
            fontWeight="bold"
            sx={{
              mb: 1,
              fontSize: '24px',
              color: '#000000',
              lineHeight: '2rem',
            }}
          >
            Files & Media
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              fontWeight: 'normal',
              fontSize: '14px',
            }}
          >
            Manage and share documents, contracts, and project files
          </Typography>
        </div>

        {/* Tab Navigation */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                color: 'text.secondary'
              },
              '& .Mui-selected': {
                color: 'primary.main !important'
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: 3
              }
            }}
          >
            <Tab label="MY UPLOADED FILES & MEDIA" />
            <Tab label="NATIVE CODERS FILES & MEDIA" />
          </Tabs>
        </Box>
        
        {/* Action Bar */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 1,
          alignItems: 'center',
          maxWidth: '70rem'
        }}>

          <TextField
            placeholder="Search files by name or comment"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '44px'
              }
            }}
          />

          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            sx={{
              height: '44px',
              px: 2,
              backgroundColor: 'white',
              borderColor: '#DEDFD1',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Filter
          </Button>
          {activeTab === 0 && (
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              sx={{
                height: '44px',
                px: 3,
                flexShrink: 0,
                whiteSpace: 'nowrap',
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }}
            >
              UPLOAD FILE
            </Button>
          )}
        </Box>

        {/* File Count */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, fontSize: '14px' }}
        >
          Showing {files.length} of {files.length} files
        </Typography>

        {/* File Cards */}
        <Box sx={{ maxWidth: '70rem' }}>
          {files.map((file) => (
            activeTab === 0 ? (
              <MyFilesCard key={file.id} file={file} />
            ) : (
              <NativeFilesCard key={file.id} file={file} />
            )
          ))}
        </Box>
      </div>
    </div>
  );
};

export default FileManager;